import { motion } from 'framer-motion'
import {
    AcademicCapIcon,
    BookOpenIcon,
    UserGroupIcon,
    QuestionMarkCircleIcon,
    SparklesIcon,
    ArrowUpIcon,
    BuildingLibraryIcon,
    UserIcon,
    GlobeAltIcon,
    CheckBadgeIcon
} from '@heroicons/react/24/outline'

const StatCard = ({ item, index }) => {
    const Icon = item.icon
    
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            whileHover={{ y: -5 }}
            className="group"
        >
            <div className={`h-full bg-gradient-to-br ${item.gradient} border-2 p-8 rounded-3xl shadow-sm hover:shadow-xl transition-all duration-300 relative overflow-hidden`}>
                {/* Decorative element */}
                <div className="absolute -right-4 -bottom-4 w-20 h-20 bg-white/20 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500"></div>
                
                <div className="relative z-10">
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 shadow-sm group-hover:scale-110 transition-transform duration-300 ${item.iconStyle}`}>
                        <Icon className="w-8 h-8" />
                    </div>

                    <div className="flex items-baseline space-x-1 mb-2">
                        <span className="text-4xl font-black text-gray-900 tracking-tight">
                            {item.value.toLocaleString()}
                        </span>
                        <span className={`text-2xl font-black ${item.color}`}>+</span>
                    </div>

                    <h3 className="text-xs font-black text-gray-400 uppercase tracking-[0.2em] mb-4">
                        {item.label}
                    </h3>

                    <div className="flex items-center space-x-2 text-[10px] font-bold text-gray-500 uppercase tracking-widest bg-white/50 px-2 py-1 rounded-full border border-gray-100 w-fit">
                        <ArrowUpIcon className="w-3 h-3 text-emerald-500" />
                        <span>Verified Growth</span>
                    </div>
                </div>
            </div>
        </motion.div>
    )
}

const StatsSection = ({ stats }) => {
    const statItems = [
        {
            id: 'universities',
            label: 'Top Universities',
            value: stats?.universities || 100,
            icon: BuildingLibraryIcon,
            gradient: 'from-blue-50 to-indigo-50 border-blue-100',
            iconStyle: 'text-blue-600 bg-blue-100',
            color: 'text-blue-600'
        },
        {
            id: 'courses',
            label: 'Specialized Programs',
            value: stats?.courses || 500,
            icon: AcademicCapIcon,
            gradient: 'from-purple-50 to-pink-50 border-purple-100',
            iconStyle: 'text-purple-600 bg-purple-100',
            color: 'text-purple-600'
        },
        {
            id: 'enquiries',
            label: 'Success Stories',
            value: stats?.enquiries || 10000,
            icon: UserGroupIcon,
            gradient: 'from-emerald-50 to-teal-50 border-emerald-100',
            iconStyle: 'text-emerald-600 bg-emerald-100',
            color: 'text-emerald-600'
        },
        {
            id: 'faqs',
            label: 'Queries Resolved',
            value: stats?.faqs || 5000,
            icon: QuestionMarkCircleIcon,
            gradient: 'from-orange-50 to-amber-50 border-orange-100',
            iconStyle: 'text-orange-600 bg-orange-100',
            color: 'text-orange-600'
        },
    ]

    const highlights = [
        { label: "Recognition", value: "UGC Approved", icon: CheckBadgeIcon, color: "text-blue-600", bg: "bg-blue-50" },
        { label: "Community", value: "50k+ Active", icon: UserIcon, color: "text-emerald-600", bg: "bg-emerald-50" },
        { label: "Presence", value: "Global Reach", icon: GlobeAltIcon, color: "text-purple-600", bg: "bg-purple-50" },
    ]

    return (
        <section className="py-16 lg:py-24 bg-white relative overflow-hidden">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-16"
                >
                    <div className="inline-flex items-center space-x-2 bg-primary-50 px-4 py-1.5 rounded-full mb-6 border border-primary-100">
                        <SparklesIcon className="w-5 h-5 text-primary-600" />
                        <span className="text-sm font-bold text-primary-700 uppercase tracking-wider">Our Global Reach</span>
                    </div>
                    <h2 className="text-4xl sm:text-5xl font-black text-gray-900 mb-6 leading-tight tracking-tight">
                        Impact & <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-accent-600">Empowerment</span>
                    </h2>
                    <p className="text-lg text-gray-600 max-w-2xl mx-auto font-medium leading-relaxed">
                        We measure our success by the growth of our students. Explore the numbers that drive our commitment to excellence in education.
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
                    {statItems.map((item, index) => (
                        <StatCard key={item.id} item={item} index={index} />
                    ))}
                </div>

                {/* Simplified Highlights Row */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                    className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-12 border-t border-gray-100"
                >
                    {highlights.map((item, index) => {
                        const Icon = item.icon
                        return (
                            <div key={index} className="flex items-center space-x-4 p-4 rounded-2xl hover:bg-gray-50 transition-colors group">
                                <div className={`w-12 h-12 rounded-xl ${item.bg} ${item.color} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}>
                                    <Icon className="w-6 h-6" />
                                </div>
                                <div>
                                    <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none mb-1">
                                        {item.label}
                                    </div>
                                    <div className="text-lg font-black text-gray-900 leading-tight">
                                        {item.value}
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </motion.div>
            </div>
        </section>
    )
}

export default StatsSection
